'use client';

import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, ChevronDown, LayoutGrid, RefreshCw } from 'lucide-react';
import Navbar from '@/components/help-center/Navbar';
import Footer from '@/components/help-center/Footer';
import { HeroBackdropLayers, cn } from '@/components/help-center/latest-power-shared';
import { useCms } from '@/components/painel/cms/cms-context';
import RegrasCategoryFilter, {
  ruleCategoryConfig,
  ruleCategoryConfigLight,
  ruleCategoryFallback,
  ruleCategoryFallbackLight,
} from '@/components/help-center/regras/RegrasCategoryFilter';
import type { ConductAppearance } from '@/lib/conduct-presentation';
import { useHelpAppearance } from '@/lib/help-appearance';
import {
  getRegrasCardInteractiveClass,
  getRegrasSurfaceClass,
  getRegrasTokens,
} from '@/lib/regras-ui-tokens';
import { PublicPageSkeleton } from '@/components/help-center/PublicPageSkeleton';

function FilledMoonIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M21.75 15.07A9.75 9.75 0 0 1 8.93 2.25a.75.75 0 0 0-.9-.9A11.25 11.25 0 1 0 22.65 15.97a.75.75 0 0 0-.9-.9Z" />
    </svg>
  );
}

function FilledSunIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <circle cx="12" cy="12" r="4.25" />
      <path d="M12 1.75a.75.75 0 0 1 .75.75v1.75a.75.75 0 0 1-1.5 0V2.5A.75.75 0 0 1 12 1.75Zm0 17.5a.75.75 0 0 1 .75.75V21.75a.75.75 0 0 1-1.5 0v-1.75a.75.75 0 0 1 .75-.75ZM22.25 12a.75.75 0 0 1-.75.75h-1.75a.75.75 0 0 1 0-1.5H21.5a.75.75 0 0 1 .75.75ZM4.25 12a.75.75 0 0 1-.75.75H1.75a.75.75 0 0 1 0-1.5H3.5A.75.75 0 0 1 4.25 12Zm15.01-7.26a.75.75 0 0 1 0 1.06l-1.24 1.24a.75.75 0 1 1-1.06-1.06l1.24-1.24a.75.75 0 0 1 1.06 0ZM7.04 16.96a.75.75 0 0 1 0 1.06l-1.24 1.24a.75.75 0 0 1-1.06-1.06l1.24-1.24a.75.75 0 0 1 1.06 0Zm11.22 1.06a.75.75 0 0 1-1.06 0l-1.24-1.24a.75.75 0 1 1 1.06-1.06l1.24 1.24a.75.75 0 0 1 0 1.06ZM7.04 7.04a.75.75 0 0 1-1.06 0L4.74 5.8A.75.75 0 0 1 5.8 4.74l1.24 1.24a.75.75 0 0 1 0 1.06Z" />
    </svg>
  );
}

export default function RegrasPage() {
  const { site, siteReady } = useCms();
  const [expandedRules, setExpandedRules] = useState<Set<string>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState('Todas');
  const { appearance, setMode, hydrated, isLight } = useHelpAppearance();

  const effectiveAppearance: ConductAppearance = hydrated ? appearance : 'dark';
  const t = getRegrasTokens(effectiveAppearance);
  const surfaceClass = getRegrasSurfaceClass(t);
  const cardClass = getRegrasCardInteractiveClass(t, effectiveAppearance);

  const richContentClass = cn(
    'font-inter text-[15px] leading-relaxed sm:text-base',
    '[&_p]:my-0 [&_p+p]:mt-3',
    '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5',
    '[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5',
    '[&_a]:underline [&_a]:underline-offset-2',
    isLight
      ? 'text-slate-600 [&_a]:text-[#000b6a] hover:[&_a]:text-[#000b6a]/80 [&_strong]:font-semibold [&_strong]:text-slate-900'
      : 'text-[#AEB7D6] [&_a]:text-sky-300 hover:[&_a]:text-sky-200 [&_strong]:font-semibold [&_strong]:text-white'
  );

  const rules = useMemo(
    () => [...site.rulesSections].sort((a, b) => a.displayOrder - b.displayOrder),
    [site.rulesSections]
  );

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { Todas: rules.length };
    for (const rule of rules) {
      counts[rule.category] = (counts[rule.category] ?? 0) + 1;
    }
    return counts;
  }, [rules]);

  const filterCategories = useMemo(() => {
    const cats = new Set(rules.map((r) => r.category));
    return [
      { name: 'Todas', count: categoryCounts.Todas },
      ...Array.from(cats)
        .sort()
        .map((name) => ({ name, count: categoryCounts[name] ?? 0 })),
    ];
  }, [rules, categoryCounts]);

  const filteredRules = useMemo(
    () =>
      selectedCategory === 'Todas'
        ? rules
        : rules.filter((r) => r.category === selectedCategory),
    [rules, selectedCategory]
  );

  function toggle(id: string) {
    setExpandedRules((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (!siteReady) {
    return (
      <div className="flex min-h-screen flex-col bg-white">
        <Navbar />
        <main className="flex-1 bg-[#030023]">
          <PublicPageSkeleton />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className={cn('flex min-h-screen flex-col', isLight ? 'bg-slate-50' : 'bg-white')}>
      <Navbar />
      <main
        className={cn(
          'flex-1 transition-colors duration-300',
          isLight ? 'bg-[#F4F7FC]' : 'bg-[#030023]'
        )}
      >
        {/* Hero — sempre escuro */}
        <section
          className={cn(
            'relative overflow-hidden bg-[#030023]',
            isLight && 'border-b border-blue-900/50'
          )}
        >
          <HeroBackdropLayers className="absolute inset-0" />
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#030023]"
            aria-hidden
          />
          <div className="pointer-events-none absolute inset-0 opacity-40" aria-hidden>
            <span className="absolute left-[12%] top-[28%] h-1 w-1 rounded-full bg-white/80 shadow-[0_0_6px_rgba(255,255,255,0.8)]" />
            <span className="absolute left-[78%] top-[34%] h-0.5 w-0.5 rounded-full bg-sky-100/70" />
            <span className="absolute left-[22%] top-[62%] h-0.5 w-0.5 rounded-full bg-white/60" />
            <span className="absolute left-[88%] top-[58%] h-1 w-1 rounded-full bg-white/50 shadow-[0_0_8px_rgba(125,211,252,0.5)]" />
            <span className="absolute left-[55%] top-[42%] h-0.5 w-0.5 rounded-full bg-white/50" />
          </div>

          <div className="relative z-10 mx-auto max-w-7xl px-6 pb-16 pt-28 sm:pb-20 sm:pt-32 md:pt-36">
            <div className="mb-4 flex items-center justify-between gap-3 sm:mb-5">
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border border-sky-400/25',
                  'bg-sky-500/10 px-3 py-1 font-inter text-[10px] font-bold uppercase tracking-[0.16em] text-sky-100/90',
                  'backdrop-blur-sm sm:text-[11px]'
                )}
              >
                <BookOpen className="h-3 w-3 text-sky-300" aria-hidden />
                Guia da comunidade
              </span>

              <div
                className="inline-flex shrink-0 items-center rounded-full border border-sky-400/25 bg-white/[0.06] p-0.5 backdrop-blur-sm"
                role="group"
                aria-label="Alternar aparência do conteúdo"
              >
                <button
                  type="button"
                  onClick={() => setMode('dark')}
                  className={cn(
                    'inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-200',
                    appearance === 'dark'
                      ? 'bg-sky-500/25 text-sky-100'
                      : 'text-sky-300/75 hover:bg-sky-400/10 hover:text-sky-100'
                  )}
                  aria-pressed={appearance === 'dark'}
                  aria-label="Modo escuro"
                  title="Modo escuro"
                >
                  <FilledMoonIcon className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setMode('light')}
                  className={cn(
                    'inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-200',
                    appearance === 'light'
                      ? 'bg-sky-500/25 text-sky-100'
                      : 'text-sky-300/75 hover:bg-sky-400/10 hover:text-sky-100'
                  )}
                  aria-pressed={appearance === 'light'}
                  aria-label="Modo claro"
                  title="Modo claro"
                >
                  <FilledSunIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            <h1 className="font-poppins text-[2rem] font-bold leading-[1.15] tracking-tight text-white sm:text-4xl md:text-5xl">
              {site.rulesPageTitle || 'Regras do xat'}
            </h1>
            <p className="mt-4 max-w-2xl font-poppins text-lg leading-relaxed text-[#B5BEDA] md:text-xl">
              {site.rulesPageSubtitle}
            </p>
          </div>
        </section>

        <RegrasCategoryFilter
          categories={filterCategories}
          selected={selectedCategory}
          onSelect={setSelectedCategory}
          appearance={effectiveAppearance}
        />

        {/* Biblioteca */}
        <section
          className={cn(
            'px-6 pb-16 pt-10 transition-colors duration-300 sm:pb-20 sm:pt-12',
            t.pageBg
          )}
        >
          <div className="mx-auto max-w-7xl">
            <header className="mb-6 sm:mb-8">
              <h2
                className={cn(
                  'font-poppins text-xl font-bold tracking-tight sm:text-2xl',
                  t.textPrimary
                )}
              >
                Biblioteca de regras
              </h2>
              <p className={cn('mt-1.5 font-inter text-sm', t.textSecondary)}>
                {filteredRules.length === 1
                  ? '1 regra nesta categoria'
                  : `${filteredRules.length} regras nesta categoria`}
              </p>
            </header>

            {filteredRules.length === 0 ? (
              <div className={cn(surfaceClass, 'flex flex-col items-center px-6 py-14 text-center')}>
                <span
                  className={cn(
                    'mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl border',
                    t.border,
                    t.accentSoft,
                    isLight ? 'text-[#000b6a]' : 'text-sky-200'
                  )}
                >
                  <LayoutGrid className="h-5 w-5" aria-hidden />
                </span>
                <h3 className={cn('font-poppins text-lg font-semibold', t.textPrimary)}>
                  Nenhuma regra nesta categoria
                </h3>
                <p className={cn('mt-2 max-w-sm font-inter text-sm', t.textSecondary)}>
                  Tente outra categoria ou volte para ver todas as regras da comunidade.
                </p>
                <button
                  type="button"
                  onClick={() => setSelectedCategory('Todas')}
                  className={cn(
                    'mt-6 rounded-full border px-4 py-2 font-poppins text-sm font-semibold transition-colors',
                    t.borderActive,
                    isLight
                      ? 'bg-[rgba(0,11,106,0.08)] text-[#000b6a] hover:bg-[rgba(0,11,106,0.12)]'
                      : 'bg-sky-500/15 text-sky-50 hover:bg-sky-500/25'
                  )}
                >
                  Ver todas as regras
                </button>
              </div>
            ) : (
              <div className={cn('flex flex-col', t.gap)}>
                {filteredRules.map((rule, listIndex) => {
                  const cfg = isLight
                    ? ruleCategoryConfigLight[rule.category] ?? ruleCategoryFallbackLight
                    : ruleCategoryConfig[rule.category] ?? ruleCategoryFallback;
                  const CategoryIcon = cfg.icon;
                  const isOpen = expandedRules.has(rule.id);
                  const number = String(rule.displayOrder).padStart(2, '0');

                  return (
                    <motion.article
                      key={`${selectedCategory}-${rule.id}`}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: '-20px' }}
                      transition={{
                        duration: 0.35,
                        delay: Math.min(listIndex * 0.03, 0.24),
                        ease: [0.22, 1, 0.36, 1],
                      }}
                      className={cn(cardClass, 'group relative overflow-hidden')}
                    >
                      <span
                        className={cn(
                          'absolute bottom-0 left-0 top-0 w-0.5 transition-colors duration-200',
                          t.sideBar
                        )}
                        aria-hidden
                      />
                      <button
                        type="button"
                        onClick={() => toggle(rule.id)}
                        aria-expanded={isOpen}
                        className="flex w-full items-start gap-3.5 px-4 py-4 text-left sm:gap-4 sm:px-5 sm:py-5"
                      >
                        <div className="flex w-11 shrink-0 flex-col items-center gap-2 sm:w-12">
                          <span
                            className={cn(
                              'font-poppins text-xl font-bold tabular-nums leading-none tracking-tight sm:text-2xl',
                              t.indexGlow
                            )}
                          >
                            {number}
                          </span>
                          <span
                            className={cn(
                              'inline-flex h-8 w-8 items-center justify-center rounded-xl border sm:h-9 sm:w-9',
                              'transition-colors duration-200',
                              t.iconShell
                            )}
                          >
                            <CategoryIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden />
                          </span>
                        </div>

                        <div className="min-w-0 flex-1 pt-0.5">
                          <div className="mb-2 flex flex-wrap items-center gap-1.5">
                            <span
                              className={cn(
                                'inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5',
                                'font-poppins text-[10px] font-semibold uppercase tracking-[0.14em]',
                                t.badge
                              )}
                            >
                              <span className={cn('h-1.5 w-1.5 rounded-full', cfg.dot)} aria-hidden />
                              {rule.category}
                            </span>
                            {rule.updated ? (
                              <span
                                className={cn(
                                  'inline-flex items-center gap-1 rounded-md border px-2 py-0.5',
                                  'font-poppins text-[10px] font-semibold uppercase tracking-[0.12em]',
                                  t.badgeUpdated
                                )}
                              >
                                <RefreshCw className="h-3 w-3" aria-hidden />
                                Atualizada
                              </span>
                            ) : null}
                          </div>

                          <h3
                            className={cn(
                              'font-poppins text-base font-semibold leading-snug sm:text-lg',
                              t.textPrimary
                            )}
                          >
                            {rule.title}
                          </h3>

                          {!isOpen ? (
                            <p className={cn('mt-2 font-inter text-sm', t.accent)}>
                              Ler regra completa
                              <span className="ml-1 inline-block transition-transform group-hover:translate-x-0.5">
                                →
                              </span>
                            </p>
                          ) : null}
                        </div>

                        <span
                          className={cn(
                            'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border',
                            'transition-colors duration-200',
                            t.chevronShell
                          )}
                          aria-hidden
                        >
                          <ChevronDown
                            className={cn(
                              'h-4 w-4 transition-transform duration-300 ease-out',
                              isOpen && 'rotate-180'
                            )}
                          />
                        </span>
                      </button>

                      <div
                        className={cn(
                          'grid transition-[grid-template-rows] duration-300 ease-out',
                          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                        )}
                      >
                        <div className="overflow-hidden">
                          <div className="relative px-4 pb-5 pt-3 sm:px-5 sm:pb-6 sm:pl-[4.75rem]">
                            <div
                              className={cn(
                                'pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b to-transparent',
                                t.expandWash
                              )}
                              aria-hidden
                            />
                            <div
                              className={cn('relative', richContentClass)}
                              dangerouslySetInnerHTML={{ __html: rule.bodyHtml }}
                            />
                            <button
                              type="button"
                              onClick={() => toggle(rule.id)}
                              className={cn(
                                'relative mt-5 font-poppins text-xs font-semibold transition-colors',
                                t.accent,
                                isLight ? 'hover:text-[rgba(0,11,106,0.75)]' : 'hover:text-sky-100'
                              )}
                            >
                              Ocultar regra
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.article>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
